import React from 'react';
import { useParams } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import { useQuery } from '@apollo/client';
import { isBefore, today, formatDate } from '@perch-labs/warbler';
import {
  Grid,
  LeftColumnGridCell,
  RightColumnGridCell,
  MobileGridCell,
} from 'styles';
import { screenIsSmall } from 'utils/screen-is-small';
import { LoadingModule } from 'components/loading';
import { ProjectTimeline } from 'pages/solar/components/project-timeline';
import { AdvocacyModule } from 'pages/solar/components/advocacy-module';
import {
  ProjectDetails,
  getProjectDetailsProps,
  Project,
  ProjectStatistics,
} from 'pages/solar/components/project-details/';
import { StateSolarFarms } from 'pages/solar/components/state-solar-farms';
import { EnergyBreakdown } from 'pages/solar/components/energy-breakdown';
import { PageHeading } from 'components/page-heading';
import {
  EnrollmentTimeline,
  EnrollmentStatus,
} from 'components/enrollment-timeline';
import { PersonalEnergyProduction } from 'pages/solar/components/personal-energy-production';
import { ReconnectUtilityNotification } from 'pages/solar/components/reconnect-utility-notification';
import { GET_WAITLIST_DATA } from './waitlist.api';
import { GET_PLAN_BILLING_INFO } from '../active.api';
import { getPersonalEnergyProductionCopy } from './waitlist.utils';

interface LoadingProps {
  header: React.ReactNode;
}

const Loading = ({ header }: LoadingProps) => (
  <>
    {header}
    <Grid data-testid="loading" width="100%" gap="24px">
      <LeftColumnGridCell>
        <LoadingModule />
        <LoadingModule margin={{ top: '24px' }} />
      </LeftColumnGridCell>
      <RightColumnGridCell>
        <LoadingModule />
        <LoadingModule margin={{ top: '24px' }} />
      </RightColumnGridCell>
    </Grid>
  </>
);

export const Waitlist: React.FC<SolarActiveViewProps> = props => {
  const {
    advocacyCampaign,
    completedAdvocacyCampaign,
    hasAdvocacyCampaignSubmission,
    pageHeading,
    stateAbbreviation,
    stateFullName,
    stateId,
    vnmSolarProjectsStatistics,
    zipCode,
    utilityAccountId,
  } = props;
  const { width } = useWindowSize();
  const { planId } = useParams<{ planId?: string }>();

  const { data: billableData, loading: billableLoading } = useQuery<
    GetPlanBillingInfoQuery,
    GetPlanBillingInfoQueryVariables
  >(GET_PLAN_BILLING_INFO, {
    skip: !planId,
    variables: { id: Number(planId) || -1 },
  });

  const { data: waitlistData, loading: waitlistLoading } = useQuery<
    GetVnmWaitlistDataQuery,
    GetVnmWaitlistDataQueryVariables
  >(GET_WAITLIST_DATA, {
    skip: !planId,
    variables: { planId: Number(planId) || -1 },
  });

  const header = <PageHeading page={pageHeading} />;

  const vnmSubscription =
    waitlistData?.plan?.vnmProductSubscription?.vnmSubscription;
  const project = vnmSubscription?.vnmSolarProject;

  const {
    name,
    startDate,
    acquisitionStatistics,
    firstYearProductionEstimate = 0,
  } = project || {};

  if (waitlistLoading || billableLoading) {
    return <Loading header={header} />;
  }

  const billable = billableData?.plan?.billable;
  const projectDetailsProps = getProjectDetailsProps(project as Project);
  const projectIsLive = isBefore(startDate, today());

  const showProjectTimeline = !projectIsLive;

  const calculatedAllocationKw =
    vnmSubscription?.mostRecentSubscriptionSubmission?.calculatedAllocationKw;
  const personalEnergyProductionCopy = getPersonalEnergyProductionCopy({
    calculatedAllocationKw,
  });

  const mobileGrid = (
    <Grid width="100%" gap="24px">
      <MobileGridCell>
        <PersonalEnergyProduction
          billable={billable}
          copy={personalEnergyProductionCopy}
          projectIsLive={projectIsLive}
          calculatedAllocationKw={calculatedAllocationKw}
        >
          <EnrollmentTimeline
            status={EnrollmentStatus.Waitlist}
            stateAbbreviation={stateAbbreviation}
            project={name}
            projectStartDate={startDate}
          />
        </PersonalEnergyProduction>
        <AdvocacyModule
          advocacyCampaign={advocacyCampaign}
          completedAdvocacyCampaign={completedAdvocacyCampaign}
          hasAdvocacyCampaignSubmission={hasAdvocacyCampaignSubmission}
          margin={{ bottom: '24px' }}
          stateAbbreviation={stateAbbreviation}
          stateFullName={stateFullName}
          utilityAccountId={utilityAccountId}
        />
        {!advocacyCampaign && (
          <ProjectDetails {...projectDetailsProps}>
            <ProjectStatistics
              homesPoweredAnnually={
                acquisitionStatistics?.estimatedHomesPowered
              }
              mwhProducedAnnually={firstYearProductionEstimate / 1000}
            />
            {showProjectTimeline && (
              <ProjectTimeline
                launchDate={formatDate(project?.startDate, {
                  format: 'MMMM yyyy',
                })}
              />
            )}
          </ProjectDetails>
        )}
        <StateSolarFarms
          stateFullName={stateFullName}
          stateId={stateId}
          vnmSolarProjectsStatistics={vnmSolarProjectsStatistics}
          vnmSolarProjectId={project?.id}
        />
        {showProjectTimeline && <EnergyBreakdown zipCode={zipCode} />}
        {advocacyCampaign && (
          <ProjectDetails {...projectDetailsProps}>
            <ProjectStatistics
              homesPoweredAnnually={
                acquisitionStatistics?.estimatedHomesPowered
              }
              mwhProducedAnnually={firstYearProductionEstimate / 1000}
            />
            {showProjectTimeline && (
              <ProjectTimeline
                launchDate={formatDate(project?.startDate, {
                  format: 'MMMM yyyy',
                })}
              />
            )}
          </ProjectDetails>
        )}
        {!showProjectTimeline && <EnergyBreakdown zipCode={zipCode} />}
      </MobileGridCell>
    </Grid>
  );

  const desktopGrid = (
    <Grid width="100%" gap="24px">
      <LeftColumnGridCell>
        <PersonalEnergyProduction
          billable={billable}
          copy={personalEnergyProductionCopy}
          projectIsLive={projectIsLive}
          calculatedAllocationKw={calculatedAllocationKw}
        >
          <EnrollmentTimeline
            status={EnrollmentStatus.Waitlist}
            stateAbbreviation={stateAbbreviation}
            project={name}
            projectStartDate={startDate}
          />
        </PersonalEnergyProduction>
        <StateSolarFarms
          stateFullName={stateFullName}
          stateId={stateId}
          vnmSolarProjectsStatistics={vnmSolarProjectsStatistics}
          vnmSolarProjectId={project?.id}
        />
        {showProjectTimeline && <EnergyBreakdown zipCode={zipCode} />}
      </LeftColumnGridCell>
      <RightColumnGridCell>
        <AdvocacyModule
          advocacyCampaign={advocacyCampaign}
          completedAdvocacyCampaign={completedAdvocacyCampaign}
          hasAdvocacyCampaignSubmission={hasAdvocacyCampaignSubmission}
          margin={{ bottom: '24px' }}
          stateAbbreviation={stateAbbreviation}
          stateFullName={stateFullName}
          utilityAccountId={utilityAccountId}
        />
        <ProjectDetails {...projectDetailsProps}>
          <ProjectStatistics
            homesPoweredAnnually={acquisitionStatistics?.estimatedHomesPowered}
            mwhProducedAnnually={firstYearProductionEstimate / 1000}
          />
          {showProjectTimeline && (
            <ProjectTimeline
              launchDate={formatDate(project?.startDate, {
                format: 'MMMM yyyy',
              })}
            />
          )}
        </ProjectDetails>
        {!showProjectTimeline && <EnergyBreakdown zipCode={zipCode} />}
      </RightColumnGridCell>
    </Grid>
  );

  return (
    <>
      {header}
      <ReconnectUtilityNotification />
      {screenIsSmall(width) ? mobileGrid : desktopGrid}
    </>
  );
};
